"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  buildPestle,
  type PestleFactor,
  type PestleInput,
  type PestleVariant,
  VARIANT_FACTORS,
} from "@/lib/tool-kit/business-templates/pestle";

/** English strings. A translated content file overrides these through `ui`. */
export const PESTLE_ANALYSIS_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  subjectHeading: "What are you analyzing?",
  subjectLabel: "Organization, industry or product",
  subjectPlaceholder: "e.g. A UK online grocery start-up",
  objectiveLabel: "Objective — the decision this analysis supports",
  objectivePlaceholder: "e.g. Decide whether to expand into Ireland",
  scopeLabel: "Market or region",
  scopePlaceholder: "e.g. United Kingdom and Ireland",
  horizonLabel: "Time horizon",
  horizonPlaceholder: "e.g. The next three years",
  variantLabel: "Version of the framework",
  variants: {
    pest: "PEST (four factors)",
    pestle: "PESTLE (six factors)",
  },
  factorsHeading: "The factors — one item per line, most important first",
  factorLabels: {
    political: "Political",
    economic: "Economic",
    social: "Social",
    technological: "Technological",
    legal: "Legal",
    environmental: "Environmental",
  },
  factorPlaceholders: {
    political: "e.g. Trade rules between the UK and Ireland",
    economic: "e.g. Falling consumer confidence",
    social: "e.g. More households ordering food online",
    technological: "e.g. Cheaper route-planning software",
    legal: "e.g. Food-labelling requirements in each country",
    environmental: "e.g. Pressure to cut delivery emissions",
  },
  factorHints: {
    political:
      "Government stability, taxes, trade rules, subsidies, regulation and lobbying.",
    economic:
      "Growth, inflation, interest rates, exchange rates, wages, spending power.",
    social:
      "Demographics, attitudes, lifestyle, education, health, cultural norms.",
    technological:
      "New technology, automation, R&D, digital infrastructure, patents.",
    legal:
      "Employment, consumer, competition, data-protection, health-and-safety law.",
    environmental:
      "Climate, weather, resources, waste, energy, sustainability expectations.",
  },
  fileName: "pestle-analysis",
  document: {
    heading: "Your PESTLE analysis",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the analysis has your content.",
  },
  defaultTitle: "PESTLE Analysis",
  titleWithSubject: "PESTLE Analysis: {subject}",
  subtitle: {
    pest: "PEST — political, economic, social, technological factors",
    pestle:
      "PESTLE — political, economic, social, technological, legal, environmental factors",
  },
  fields: {
    objective: "Objective",
    scope: "Market or region",
    horizon: "Time horizon",
  },
  contextPrompt:
    "Say what the analysis is for, which market or region it covers and how far ahead it looks. A PESTLE with no objective, place or date cannot be checked or ranked.",
  captions: {
    political: "Government and policy",
    economic: "Money and markets",
    social: "People and culture",
    technological: "Tools and innovation",
    legal: "Laws and regulation",
    environmental: "Nature and resources",
  },
  factorPrompts: {
    political:
      "Which governments or policies shape this market? Think of taxes, trade rules, subsidies, stability and lobbying.",
    economic:
      "How do growth, inflation, interest rates, exchange rates and spending power affect demand and costs?",
    social:
      "Who are the customers, and how are their numbers, attitudes, habits and expectations changing?",
    technological:
      "What new technology or automation could change how this is made, sold or used — and who has access to it?",
    legal:
      "Which laws and regulations apply — employment, consumer, competition, data, safety — and what is changing?",
    environmental:
      "How do climate, resources, waste, energy costs and sustainability expectations affect this?",
  },
  gridNote:
    "Write each factor as a specific, dated fact or trend — “UK consumer confidence fell in 2025” — rather than a topic such as “the economy”. Rank the items inside each box by effect on your objective.",
  ratingHeading: "Rate Each Factor",
  ratingNote:
    "Fill the last two columns by hand or in your document. Impact: high, medium or low effect on the objective. Timing: already happening, within the horizon, or beyond it.",
  ratingHead: {
    factor: "Factor",
    item: "Item",
    impact: "Impact (H / M / L)",
    timing: "Timing",
  },
  ratingPrompt:
    "Add items to the boxes above and each one appears here, ready to rate.",
  soWhatHeading: "So What?",
  soWhatNote:
    "A PESTLE grid describes the environment; it does not say what to do. Answer these in order, using the items you rated high.",
  soWhatSteps: [
    "Which three items matter most for the objective, and why?",
    "For each, is it an opportunity, a threat, or both — and for whom?",
    "How certain is each one: confirmed, likely, or a possibility?",
    "What would you do differently because of it — and what would change your mind?",
  ],
  sourcesHeading: "Sources",
  sourcesNote:
    "Every factor is a claim about the outside world. Name where each one comes from so a reader can check it.",
  sourcesPrompt:
    "List a source for each item: author or organization, year, and title. Prefer official statistics, legislation, regulators' reports and industry bodies over blogs.",
  example: {
    subject: "A UK online grocery start-up",
    objective: "Decide whether to expand into Ireland within three years",
    scope: "United Kingdom and Ireland",
    horizon: "The next three years",
    political:
      "Trade rules for food crossing the Irish Sea\nGovernment support for small exporters",
    economic:
      "Falling consumer confidence and tighter household budgets\nExchange-rate swings between sterling and the euro",
    social:
      "More households ordering groceries online\nRising interest in locally sourced food",
    technological:
      "Cheaper route-planning and warehouse software\nCustomers expect two-hour delivery slots",
    legal:
      "Food-labelling and allergen rules differ between the two countries\nData-protection rules for customer accounts",
    environmental:
      "Pressure to cut delivery emissions\nSupermarkets phasing out single-use packaging",
  },
} as const;

const FACTOR_IDS: Record<PestleFactor, string> = {
  political: "pe-political",
  economic: "pe-economic",
  social: "pe-social",
  technological: "pe-technological",
  legal: "pe-legal",
  environmental: "pe-environmental",
};

const EMPTY: PestleInput = {
  subject: "",
  objective: "",
  scope: "",
  horizon: "",
  variant: "pestle",
  political: "",
  economic: "",
  social: "",
  technological: "",
  legal: "",
  environmental: "",
};

type TextKey = "subject" | "objective" | "scope" | "horizon" | PestleFactor;

export default function PestleAnalysisGeneratorWidget() {
  const strings = useToolUi(PESTLE_ANALYSIS_GENERATOR_STRINGS);
  const [input, setInput] = useState<PestleInput>(EMPTY);
  const doc = useMemo(() => buildPestle(input, strings), [input, strings]);
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3 print:hidden">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({
              ...EMPTY,
              ...strings.example,
              variant: previous.variant,
            }))
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <div className="space-y-8 print:hidden">
        <Panel title={strings.subjectHeading}>
          <TextField
            id="pe-subject"
            label={strings.subjectLabel}
            onChange={update("subject")}
            placeholder={strings.subjectPlaceholder}
            value={input.subject}
          />
          <TextField
            id="pe-objective"
            label={strings.objectiveLabel}
            onChange={update("objective")}
            placeholder={strings.objectivePlaceholder}
            value={input.objective}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="pe-scope"
              label={strings.scopeLabel}
              onChange={update("scope")}
              placeholder={strings.scopePlaceholder}
              value={input.scope}
            />
            <TextField
              id="pe-horizon"
              label={strings.horizonLabel}
              onChange={update("horizon")}
              placeholder={strings.horizonPlaceholder}
              value={input.horizon}
            />
          </div>
          <PillGroup<PestleVariant>
            label={strings.variantLabel}
            name="pe-variant"
            onChange={(variant) =>
              setInput((previous) => ({ ...previous, variant }))
            }
            options={(["pestle", "pest"] as const).map((value) => ({
              value,
              label: strings.variants[value],
            }))}
            value={input.variant}
          />
        </Panel>

        <Panel title={strings.factorsHeading}>
          <div className="grid gap-4 sm:grid-cols-2">
            {VARIANT_FACTORS[input.variant].map((factor) => (
              <TextAreaField
                hint={strings.factorHints[factor]}
                id={FACTOR_IDS[factor]}
                key={factor}
                label={strings.factorLabels[factor]}
                onChange={update(factor)}
                placeholder={strings.factorPlaceholders[factor]}
                prose
                rows={5}
                value={input[factor]}
              />
            ))}
          </div>
        </Panel>
      </div>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="pe"
        labels={strings.document}
      />
    </div>
  );
}
